/**
 * The product data presets: the Garmin (`:~garmin:*`) and Whoop
 * (`:~whoop:*`) chart presets and the host-monitor presets
 * (`:system.host[*].*`). The server names the same lookup keys. Built in
 * (`SheetRegistries.BUILTINS` through `data-presets.ts`), so every host and
 * the CLI render them alike.
 */
import {
  DateTime,
  MsSinceEpoch,
  NaiveDate,
  NaiveDateTime,
  Time,
  TimeOfDay,
  TimezoneRegion,
  Utc,
} from "@tento-chrono";
import type { DataPresetSpec } from "../../rows/data-preset-spec.ts";
import { BY_MSE, BY_DSE, mseDisplay, dseDisplay } from "./data-preset-axes.ts";

const BUCKET_SIZE_MS = Time.MS_PER_HR;

export const PRODUCT_DATA_PRESETS: ReadonlyMap<string, readonly DataPresetSpec[]> = new Map<string, readonly DataPresetSpec[]>([
// ─── Garmin Stress ───────────────────────────────────────────────
[":~garmin:stress", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 0, max: 100, center: 50 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-band", attributes: { id: "band" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", color: "#fe887c" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    // This series extracts via a Lisp formula
    // (`dataSource:"formula"`, scope `row`) instead of the `_adapter`
    // closure. `(json-get entry "overall")` returns the Y; X is the
    // cell date's dse; a missing/null field skips the point — exactly
    // what the closure below did.
    formula: `(json-get entry "overall")`,
    attributes: {
      source: ":~garmin:stress",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
]],

// ─── Garmin Hydration ────────────────────────────────────────────
[":~garmin:hydration", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_MSE,
      xDisplay: mseDisplay,
    },
    attributes: {
      yAxis: { min: 1000, max: 5000, center: 3000 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2 } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    // C1: single-Y formula with an mse x-axis (intraday-style). `xSource`
    // selects `date.mse` over the default `date.dse`.
    formula: `(json-get entry "goalInML")`,
    attributes: {
      source: ":~garmin:hydration",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
      xSource: "date.mse",
    },
  },
]],

// ─── Garmin HRV ──────────────────────────────────────────────────
[":~garmin:hrv", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_MSE,
      xDisplay: mseDisplay,
    },
    attributes: {
      xPadding: { days: 14 },
      yAxis: { min: 50, max: 150, center: 100 },
    },
    children: [
      { type: "canvas.points" },
      { type: "canvas.hrule-all", attributes: { offset: 0.1 } },
      { type: "canvas.regression-fill", attributes: { id: "fill", offset: 0.1 } },
      { type: "canvas.regression-points", attributes: { id: "reg-points" } },
      { type: "canvas.regression-band", attributes: { id: "band" } },
      { type: "canvas.regression-curve", attributes: { id: "curve" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    // fan-out timestamp-X formula. `map` over the intraday hrvReadings,
    // each → [date.mse(date.utc(readingTimeGMT)), hrvValue]; coercePoints
    // spreads them into many [x, y] points from one daily cell. Missing
    // hrvReadings → json-get nil → empty (parity with the `?? []`). (Minor:
    // the closure skips a single unparseable reading; the formula's per-cell
    // catch drops the whole cell — real readings always parse.)
    formula:
      `(map (lambda (r) (list (date.mse (date.utc (json-get r "readingTimeGMT"))) (json-get r "hrvValue"))) (json-get entry "hrvReadings"))`,
    attributes: {
      source: ":~garmin:hrv",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
      convHalfWidth: 5 * Time.MS_PER_HR,
    },
  },
]],

// ─── Garmin Sleep ────────────────────────────────────────────────
[":~garmin:sleep", [
  {
    type: "data-canvas",
    graph: {
      toXRange: (r: NaiveDate.Range, tz: TimezoneRegion) => ({
        start: Math.floor(tz.toWallClock(r.start.withTime(TimeOfDay.ZERO)).mse / 1000),
        end: Math.floor(tz.toWallClock(r.end.withTime(TimeOfDay.ZERO)).mse / 1000),
      }),
      xDisplay: (x: number, tz: TimezoneRegion) => {
        const dt = DateTime.fromMse(MsSinceEpoch.of(x * 1000), Utc);
        const local = tz.toTz(dt);
        return local.ndt.toString().slice(0, 16).replace("T", " ");
      },
    },
    attributes: {
      xPadding: { days: 14 },
      yAxis: { min: 0, max: 5, center: 0 },
    },
    children: [
      { type: "canvas.bar-chart-with-width", attributes: { id: "bars", key: "durations" } },
      { type: "canvas.points" },
      { type: "canvas.hrule-all", attributes: { offset: 0.2, offsetType: "abs", center: 1.25 } },
      { type: "canvas.regression-fill", attributes: { id: "fill", offset: 0.2, offsetType: "abs", center: 1.25 } },
      { type: "canvas.regression-points", attributes: { id: "reg-points" } },
      { type: "canvas.regression-curve", attributes: { id: "curve" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    // C1: fan-out formula (scope row → many `[x, y]` points from one cell).
    // `map` over the intraday entries; x = entry timestamp, y = level+1.
    // Round-param `(lambda (e) …)` — bracket-param `[e]` does not bind. A
    // missing `sleepLevels` makes `json-get-in` → 0 → `map` throws → the
    // adapter skips (→ []), matching the closure's `?? []`.
    formula:
      `(map (lambda (e) (list (nth e 1) (+ (nth e 0) 1))) (json-get-in entry ["sleepLevels" "entries"]))`,
    attributes: {
      source: ":~garmin:sleep",
      seriesKey: "default",
      seriesType: "regression",
      convHalfWidth: 10 * 1_000,
      dataSource: "formula",
    },
  },
  {
    type: "data-series",
    label: "durations",
    // bar-width fan-out — `map` over the intraday entries, each →
    // `[timestamp, [level+1, duration]]`. Row scope; X from the entry
    // timestamp (`nth e 1`), Y is the `(list value width)` 2-list typed by
    // `shape:"bar-width"`. Round-param `(lambda (e) …)` — bracket-param
    // `[e]` does not bind (see the "default" series above).
    formula:
      `(map (lambda (e) (list (nth e 1) (list (+ (nth e 0) 1) (nth e 2)))) (json-get-in entry ["sleepLevels" "entries"]))`,
    attributes: {
      source: ":~garmin:sleep",
      seriesKey: "durations",
      seriesType: "default",
      dataSource: "formula",
      shape: "bar-width",
    },
  },
]],

// ─── Garmin Sleep (STS) ──────────────────────────────────────────
[":~garmin:sleep (sts)", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      xPadding: { days: 14 },
      // sleepTimeSeconds typically falls in 18000-36000s (5-10 hr).
      yAxis: { min: 18000, max: 36000, center: 27000 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-band", attributes: { id: "band" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", color: "#fe887c" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    // Track 2 / C1: nested scope:"row" formula. `json-get-in` now
    // returns nil (not 0) for a missing path, so a missing
    // `dailySleepDTO.sleepTimeSeconds` skips the point — parity with the
    // closure's `== null` guard.
    formula: `(json-get-in entry ["dailySleepDTO" "sleepTimeSeconds"])`,
    attributes: {
      source: ":~garmin:sleep (sts)",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
]],

// ─── Garmin SpO2 ────────────────────────────────────────────────
[":~garmin:spo2", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 85, max: 100, center: 95 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-band", attributes: { id: "band" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", color: "#fe887c" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    // C1: single-Y formula (scope row).
    formula: `(json-get entry "averageSpO2")`,
    attributes: {
      source: ":~garmin:spo2",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
]],

// ─── Garmin Weight ──────────────────────────────────────────────
[":~garmin:weight", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 40, max: 150, center: 80 },
    },
    children: [
      { type: "canvas.points", attributes: { id: "weight-points", key: "weight", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-curve", attributes: { id: "weight-curve", key: "weight", color: "#fe887c" } },
      { type: "canvas.points", attributes: { id: "fat-points", key: "bodyFat", radius: 2, color: "#cbe9c8" } },
      { type: "canvas.regression-curve", attributes: { id: "fat-curve", key: "bodyFat", color: "#51b748" } },
    ],
  },
  {
    type: "data-series",
    label: "weight",
    // C1: single-Y formula with arithmetic. A missing field makes the
    // division throw, which the adapter catches → point skipped, matching
    // the closure's `weight == null` guard.
    formula: `(/ (json-get entry "weight") 1000)`,
    attributes: {
      source: ":~garmin:weight",
      seriesKey: "weight",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
  {
    type: "data-series",
    label: "bodyFat",
    formula: `(json-get entry "bodyFat")`,
    attributes: {
      source: ":~garmin:weight",
      seriesKey: "bodyFat",
      seriesType: "regression",
      yRange: { min: 0, max: 50, center: 25 },
      dataSource: "formula",
    },
  },
]],

// ─── Garmin Intensity Minutes ───────────────────────────────────
[":~garmin:intensity-minutes", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 0, max: 300, center: 150 },
    },
    children: [
      { type: "canvas.points", attributes: { id: "mod-points", key: "moderate", radius: 2, color: "#cbe9c8" } },
      { type: "canvas.regression-curve", attributes: { id: "mod-curve", key: "moderate", color: "#51b748" } },
      { type: "canvas.points", attributes: { id: "vig-points", key: "vigorous", radius: 2, color: "#c2b3ff" } },
      { type: "canvas.regression-curve", attributes: { id: "vig-curve", key: "vigorous", color: "#8e7cfe" } },
    ],
  },
  {
    type: "data-series",
    label: "moderate",
    formula: `(json-get entry "moderateValue")`,
    attributes: {
      source: ":~garmin:intensity-minutes",
      seriesKey: "moderate",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
  {
    type: "data-series",
    label: "vigorous",
    formula: `(json-get entry "vigorousValue")`,
    attributes: {
      source: ":~garmin:intensity-minutes",
      seriesKey: "vigorous",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
]],

// ─── Garmin Training Readiness ──────────────────────────────────
[":~garmin:training-readiness", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 0, max: 100, center: 50 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-band", attributes: { id: "band" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", color: "#fe887c" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    // C1: single-Y formula (scope row).
    formula: `(json-get entry "trainingReadinessValue")`,
    attributes: {
      source: ":~garmin:training-readiness",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
]],

// ─── Garmin Training Status ─────────────────────────────────────
[":~garmin:training-status", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 0, max: 70, center: 45 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#cbe9c8" } },
      { type: "canvas.regression-band", attributes: { id: "band" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", color: "#51b748" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    formula: `(json-get entry "vo2Max")`,
    attributes: {
      source: ":~garmin:training-status",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
]],

// ─── Garmin Fitness Age ─────────────────────────────────────────
[":~garmin:fitness-age", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 15, max: 80, center: 40 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#c2b3ff" } },
      { type: "canvas.regression-band", attributes: { id: "band" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", color: "#8e7cfe" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    formula: `(json-get entry "fitnessAge")`,
    attributes: {
      source: ":~garmin:fitness-age",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
]],

// ─── Garmin Endurance Score ─────────────────────────────────────
[":~garmin:endurance-score", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 0, max: 100, center: 50 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#cbe9c8" } },
      { type: "canvas.regression-band", attributes: { id: "band" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", color: "#51b748" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    formula: `(json-get entry "enduranceScore")`,
    attributes: {
      source: ":~garmin:endurance-score",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
]],

// ─── Garmin Hill Score ──────────────────────────────────────────
[":~garmin:hill-score", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 0, max: 100, center: 50 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#c2b3ff" } },
      { type: "canvas.regression-band", attributes: { id: "band" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", color: "#8e7cfe" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    formula: `(json-get entry "hillScore")`,
    attributes: {
      source: ":~garmin:hill-score",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
    },
  },
]],

// ─── Garmin Race Predictions ────────────────────────────────────
[":~garmin:race-predictions", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 0, max: 300, center: 150 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-band", attributes: { id: "band" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", color: "#fe887c" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    attributes: {
      source: ":~garmin:race-predictions",
      seriesKey: "default",
      seriesType: "regression",
    },
  },
]],

// ─── Whoop Sleep ─────────────────────────────────────────────────
[":~whoop:sleep", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_MSE,
      xDisplay: (x: number) => {
        const ndt = DateTime.fromMse(MsSinceEpoch.of(x), Utc).ndt;
        return ndt.toString().slice(0, 16).replace("T", " ");
      },
    },
    attributes: {
      xPadding: { days: 14 },
      yAxis: { min: 0, max: 100, center: 0 },
    },
    children: [
      { type: "canvas.hrule", attributes: { id: "hrule-0", offset: 0, offsetType: "abs" } },
      { type: "canvas.hrule", attributes: { id: "hrule-100", offset: 100, offsetType: "abs" } },
      { type: "canvas.points", attributes: { id: "perf-points", key: "performance", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-curve", attributes: { id: "perf-curve", key: "performance", color: "#fe887c" } },
      { type: "canvas.points", attributes: { id: "eff-points", key: "efficiency", radius: 2, color: "#cbe9c8" } },
      { type: "canvas.regression-curve", attributes: { id: "eff-curve", key: "efficiency", color: "#51b748" } },
    ],
  },
  {
    type: "data-series",
    label: "performance",
    // Track 2 / C1: timestamp-X formula. X is parsed from the JSON
    // `start` timestamp via `(date.mse (date.utc …))` (matches the closure's
    // `NaiveDateTime.parse(start).mse`), Y is the nested score; the single
    // `[x, y]` point is returned through the fan-out list path. A missing
    // score/field → json-get-in nil → skipped (parity with the `== null`
    // guard).
    formula:
      `(list (list (date.mse (date.utc (json-get entry "start"))) (json-get-in entry ["score" "sleep_performance_percentage"])))`,
    attributes: {
      source: ":~whoop:sleep",
      seriesKey: "performance",
      seriesType: "regression",
      dataSource: "formula",
      convHalfWidth: Time.MS_PER_DAY * 4,
    },
  },
  {
    type: "data-series",
    label: "efficiency",
    formula:
      `(list (list (date.mse (date.utc (json-get entry "start"))) (json-get-in entry ["score" "sleep_efficiency_percentage"])))`,
    attributes: {
      source: ":~whoop:sleep",
      seriesKey: "efficiency",
      seriesType: "regression",
      dataSource: "formula",
      convHalfWidth: Time.MS_PER_DAY * 4,
    },
  },
]],

// ─── Whoop Recovery ──────────────────────────────────────────────
[":~whoop:recovery", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_MSE,
      xDisplay: mseDisplay,
    },
    attributes: {
      xPadding: { days: 14 },
      yAxis: { min: 0, max: 200, center: 0 },
    },
    children: [
      { type: "canvas.points", attributes: { id: "recovery-points", key: "recovery", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-curve", attributes: { id: "recovery-curve", key: "recovery", color: "#fe887c" } },
      { type: "canvas.points", attributes: { id: "hrv-points", key: "hrv", radius: 2, color: "#cbe9c8" } },
      { type: "canvas.regression-curve", attributes: { id: "hrv-curve", key: "hrv", color: "#51b748" } },
    ],
  },
  {
    type: "data-series",
    label: "recovery",
    // timestamp-X formula — X from json.created_at, Y nested score.
    formula:
      `(list (list (date.mse (date.utc (json-get entry "created_at"))) (json-get-in entry ["score" "recovery_score"])))`,
    attributes: {
      source: ":~whoop:recovery",
      seriesKey: "recovery",
      seriesType: "regression",
      dataSource: "formula",
      convHalfWidth: Time.MS_PER_DAY * 4,
    },
  },
  {
    type: "data-series",
    label: "hrv",
    formula:
      `(list (list (date.mse (date.utc (json-get entry "created_at"))) (json-get-in entry ["score" "hrv_rmssd_milli"])))`,
    attributes: {
      source: ":~whoop:recovery",
      seriesKey: "hrv",
      seriesType: "regression",
      yRange: { min: 0, max: 200, center: 100 },
      dataSource: "formula",
      convHalfWidth: Time.MS_PER_DAY * 4,
    },
  },
]],

// ─── Whoop Cycle ─────────────────────────────────────────────────
[":~whoop:cycle", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_MSE,
      xDisplay: mseDisplay,
    },
    attributes: {
      xPadding: { days: 14 },
      yAxis: { min: 0, max: 21, center: 0 },
    },
    children: [
      { type: "canvas.hrule", attributes: { id: "hrule-0", offset: 0, offsetType: "abs" } },
      { type: "canvas.hrule", attributes: { id: "hrule-21", offset: 21, offsetType: "abs" } },
      { type: "canvas.points", attributes: { id: "strain-points", key: "strain", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-curve", attributes: { id: "strain-curve", key: "strain", color: "#fe887c" } },
    ],
  },
  {
    type: "data-series",
    label: "strain",
    // timestamp-X formula — X from json.start, Y = score.strain.
    formula:
      `(list (list (date.mse (date.utc (json-get entry "start"))) (json-get-in entry ["score" "strain"])))`,
    attributes: {
      source: ":~whoop:cycle",
      seriesKey: "strain",
      seriesType: "regression",
      dataSource: "formula",
      convHalfWidth: Time.MS_PER_DAY * 4,
    },
  },
]],

// ─── Whoop Workout ───────────────────────────────────────────────
[":~whoop:workout", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_MSE,
      xDisplay: mseDisplay,
    },
    attributes: {
      xPadding: { days: 14 },
      yAxis: { min: 0, max: 21, center: 0 },
    },
    children: [
      { type: "canvas.hrule", attributes: { id: "hrule-0", offset: 0, offsetType: "abs" } },
      { type: "canvas.hrule", attributes: { id: "hrule-21", offset: 21, offsetType: "abs" } },
      { type: "canvas.points", attributes: { id: "strain-points", key: "strain", radius: 2, color: "#c2b3ff" } },
      { type: "canvas.regression-curve", attributes: { id: "strain-curve", key: "strain", color: "#8e7cfe" } },
    ],
  },
  {
    type: "data-series",
    label: "strain",
    // timestamp-X formula — X from json.start, Y = score.strain.
    formula:
      `(list (list (date.mse (date.utc (json-get entry "start"))) (json-get-in entry ["score" "strain"])))`,
    attributes: {
      source: ":~whoop:workout",
      seriesKey: "strain",
      seriesType: "regression",
      dataSource: "formula",
      convHalfWidth: Time.MS_PER_DAY * 4,
    },
  },
]],

// ─── System Health ───────────────────────────────────────────────
[":system.host[*].health", [
  {
    type: "data-canvas",
    graph: {
      toXRange: BY_MSE,
      xDisplay: mseDisplay,
    },
    attributes: {
      xPadding: { days: 14 },
      yAxis: { min: 0, max: 100, center: 0 },
    },
    children: [
      { type: "canvas.hrule", attributes: { id: "hrule-0", offset: 0, offsetType: "abs" } },
      { type: "canvas.hrule", attributes: { id: "hrule-100", offset: 100, offsetType: "abs" } },
      { type: "canvas.points", attributes: { id: "cpu-points", key: "default", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-curve", attributes: { id: "cpu-curve", key: "default", color: "#fe887c" } },
      { type: "canvas.regression-band", attributes: { id: "cpu-band", key: "default" } },
      { type: "canvas.points", attributes: { id: "disk-points", key: "disk", radius: 2, color: "#cbe9c8" } },
      { type: "canvas.regression-curve", attributes: { id: "disk-curve", key: "disk", color: "#51b748" } },
      { type: "canvas.regression-band", attributes: { id: "disk-band", key: "disk" } },
      { type: "canvas.points", attributes: { id: "memory-points", key: "memory", radius: 2, color: "#e1e1fe" } },
      { type: "canvas.regression-curve", attributes: { id: "memory-curve", key: "memory", color: "#999cff" } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    // Track 2 / C1: scope:"entry" Lisp formula — evaluated once per
    // DTR cell. Y = the nested percent via `json-get-in`; X = dtr.start.mse
    // (supplied by the entry adapter, not the formula). The closure is
    // cpu/disk/memory are always present, so `json-get-in`'s
    // missing→0 never bites here (and a missing key reads as null regardless).
    formula: `(json-get-in entry ["cpu" "percent"])`,
    attributes: {
      source: ":system.host[*].health",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
      scope: "entry",
      convHalfWidth: Time.MS_PER_HR * 4,
    },
  },
  {
    type: "data-series",
    label: "disk",
    formula: `(json-get-in entry ["disk" "percent"])`,
    attributes: {
      source: ":system.host[*].health",
      seriesKey: "disk",
      seriesType: "regression",
      dataSource: "formula",
      scope: "entry",
      convHalfWidth: Time.MS_PER_HR * 4,
    },
  },
  {
    type: "data-series",
    label: "memory",
    formula: `(json-get-in entry ["memory" "percent"])`,
    attributes: {
      source: ":system.host[*].health",
      seriesKey: "memory",
      seriesType: "regression",
      dataSource: "formula",
      scope: "entry",
      convHalfWidth: Time.MS_PER_HR * 4,
    },
  },
]],

// ─── System Uptime ───────────────────────────────────────────────
[":system.host[*].uptime", [
  {
    type: "data-canvas",
    graph: {
      toXRange: (r: NaiveDate.Range, tz: TimezoneRegion) => {
        const startTime = tz.toWallClock(r.start.withTime(TimeOfDay.ZERO)).toUtc();
        const endTime = tz.toWallClock(r.end.withTime(TimeOfDay.ZERO)).toUtc();
        return {
          start: Math.floor(startTime.mse / BUCKET_SIZE_MS),
          end: Math.ceil(endTime.mse / BUCKET_SIZE_MS),
        };
      },
      xDisplay: (x: number, tz: TimezoneRegion) => {
        const mse = x * BUCKET_SIZE_MS;
        const dt = DateTime.fromMse(MsSinceEpoch.of(mse), Utc);
        const local = tz.toTz(dt);
        return local.ndt.toString().slice(0, 16).replace("T", " ");
      },
    },
    attributes: {
      xPadding: { days: 0 },
      yAxis: { min: -0.33, max: 1.33, center: 0 },
    },
    children: [
      { type: "canvas.buckets", attributes: { key: "buckets" } },
      { type: "canvas.points", attributes: { key: "default", radius: 1 } },
    ],
  },
  {
    type: "data-series",
    label: "default",
    // scope:"sheet" group-by aggregate. One eval over all cells:
    // bucket the `[x, entry]` pairs (x = mse) by hour (`floor(x / 3.6e6)`),
    // then per bucket emit `[bucketIndex, mean(domain_reachable ? 1 : 0)]`.
    // `data` arrives sorted ascending by x, so `group-by` yields buckets in
    // ascending index order — matching the closure's sort.
    formula: `(map
  (lambda (b)
    (list (nth b 0)
          (/ (reduce + 0 (map (lambda (p) (if (json-get (nth p 1) "domain_reachable") 1 0)) (nth b 1)))
             (length (nth b 1)))))
  (group-by (lambda (p) (floor (/ (nth p 0) 3600000))) data))`,
    attributes: {
      source: ":system.host[*].uptime",
      seriesKey: "default",
      seriesType: "regression",
      dataSource: "formula",
      scope: "sheet",
    },
  },
  {
    type: "data-series",
    label: "buckets",
    // per-cell, no aggregation → scope:"entry" fan-out. X = the cell's
    // fractional hour position (`dtr.start / 3.6e6`, supplied by the formula,
    // not floored), Y = domain_reachable ? 1 : 0.
    formula:
      `(list (list (/ dtr.start 3600000) (if (json-get entry "domain_reachable") 1 0)))`,
    attributes: {
      source: ":system.host[*].uptime",
      seriesKey: "buckets",
      seriesType: "default",
      dataSource: "formula",
      scope: "entry",
    },
  },
]],
]);
